import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BlockStack, Banner, Button, InlineStack, Select } from '@shopify/polaris';
import EditableStoryCard from './EditableStoryCard';
import EditModeBar from './EditModeBar';
import OrderEditReview from './OrderEditReview';
import StorySwapMode from './StorySwapMode';
import { ordersService } from '../../../services/ordersService';
import { useOrderEditPreview } from '../../../hooks/useOrderEditPreview';
import {
  buildDraft,
  canRemoveStory,
  countChanges,
  getDraftErrors,
  toDesiredState
} from '../../../utils/orderEditDraft';
import { validateEntry } from '../../../utils/storefrontOrder/validation';
import { getOrderKids } from '../../../utils/orderKids';

const INCOMPLETE_STORY_ERROR = 'Finish the new and replacement stories before reviewing';
// Matches the backend rule: cities are stored in English or Arabic, often with a district.
const EXPRESS_CITY_NAMES = ['cairo', 'giza', 'القاهر', 'الجيز'];
const EXPRESS_CITY_ERROR = 'Express shipping is only available in Cairo and Giza';
const isExpressCity = city => {
  const normalized = String(city || '').trim().toLowerCase();
  return EXPRESS_CITY_NAMES.some(name => normalized.includes(name));
};

const makeRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `edit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const makeNewStory = n => ({
  key: `new-${n}`,
  lineItemId: null,
  line: null,
  catalogStory: null,
  variant: null,
  premium: false,
  premiumOriginal: false,
  addons: { giftBox: false, giftNote: false, giftMessage: '' },
  addonsOriginal: { giftBox: false, giftNote: false, giftMessage: '' },
  addonsAmbiguous: false,
  removed: false,
  replacement: null,
  newEntry: {
    key: `new-${n}-entry`,
    story: null,
    variant: null,
    languageCode: 'en',
    kidName: '',
    photos: []
  }
});

const hasIncompleteEntry = draft => draft.stories.some(story => {
  if (story.removed) return false;
  const entry = story.newEntry || story.replacement;
  return Boolean(entry) && validateEntry(entry).length > 0;
});

const isConflict = error => /order changed/i.test(error?.message || '');

export default function OrderEditMode({ order, catalog, capabilities, onExit, onSaved, onReload }) {
  const [draft, setDraft] = useState(() => buildDraft(order, catalog));
  const kids = useMemo(() => getOrderKids(order), [order]);
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [total, setTotal] = useState(null);
  const requestIdRef = useRef(makeRequestId());
  const savingRef = useRef(false);
  const newCountRef = useRef(0);

  const [shippingOptions, setShippingOptions] = useState([]);
  const canChangeShipping = capabilities.canAdd;

  useEffect(() => {
    if (!canChangeShipping) return undefined;
    let active = true;
    Promise.resolve()
      .then(() => ordersService.getShippingOptions())
      .then(options => { if (active) setShippingOptions(options); })
      .catch(() => { if (active) setShippingOptions([]); });
    return () => { active = false; };
  }, [canChangeShipping]);

  const errors = useMemo(() => {
    const list = getDraftErrors(draft);
    if (hasIncompleteEntry(draft)) list.push(INCOMPLETE_STORY_ERROR);
    if (draft.shipping?.code === 'express_shipping' && !isExpressCity(order?.shipping_address?.city)) {
      list.push(EXPRESS_CITY_ERROR);
    }
    return list;
  }, [draft, order]);
  const changeCount = countChanges(draft);

  // Past the staged-edit window only the immediate story swap remains.
  const swapOnly = !capabilities.canAdd && !capabilities.canReplace && !capabilities.canRemove
    && !capabilities.canSwitchPrinting && !capabilities.canToggleAddons && !capabilities.canDiscount
    && capabilities.canSwapStory === true;

  const handleReload = async () => {
    setConflict(false);
    await onReload();
    onExit();
  };

  const body = useMemo(
    () => (reviewing && errors.length === 0 ? toDesiredState(draft) : null),
    [reviewing, errors, draft]
  );
  const { preview, status, error: previewError } = useOrderEditPreview(order.order_id, body);

  useEffect(() => {
    if (preview) setTotal(Number(preview.totals.after));
  }, [preview]);

  // Every edit changes what would be saved, so it must not reuse an earlier idempotency key.
  const change = useCallback(update => {
    requestIdRef.current = makeRequestId();
    setTotal(null);
    setDraft(update);
  }, []);

  const updateStory = useCallback(next => change(current => ({
    ...current,
    stories: current.stories.map(story => (story.key === next.key ? next : story))
  })), [change]);

  const addStory = () => {
    newCountRef.current += 1;
    const story = makeNewStory(newCountRef.current);
    change(current => ({ ...current, stories: [...current.stories, story] }));
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
      const result = await ordersService.saveOrderEdit(
        order.order_id,
        toDesiredState(draft, requestIdRef.current)
      );
      onSaved(result);
      onExit();
    } catch (error) {
      savingRef.current = false;
      setSaving(false);
      if (isConflict(error)) {
        requestIdRef.current = makeRequestId();
        setConflict(true);
        setReviewing(false);
        return;
      }
      const details = error?.details;
      const detailText = details
        ? (typeof details === 'string' ? details : JSON.stringify(details))
        : '';
      setSaveError([error?.message || 'Failed to save order changes', detailText].filter(Boolean).join(': '));
    }
  };

  // Swap-only mode: rendered after every hook so an order prop that changes
  // mid-edit cannot reshuffle the hook order.
  if (swapOnly) {
    return <StorySwapMode order={order} onExit={onExit} onReload={onReload} />;
  }

  return (
    <BlockStack gap="400">
      <Banner tone="info" title={`Editing order #${order.order_id}`} />

      {conflict && (
        <Banner
          tone="warning"
          title="Order changed, reload"
          action={{ content: 'Reload order', onAction: handleReload }}
        >
          <p>This order was changed by someone else. Reloading discards your edits.</p>
        </Banner>
      )}

      {errors.length > 0 && (
        <Banner tone="critical" title="Fix these before reviewing">
          <BlockStack gap="100">
            {errors.map(message => <p key={message}>{message}</p>)}
          </BlockStack>
        </Banner>
      )}

      {draft.stories.map((story, index) => (
        <EditableStoryCard
          key={story.key}
          story={story}
          index={index}
          catalog={catalog}
          capabilities={capabilities}
          onChange={updateStory}
          canRemove={canRemoveStory(draft, story.key)}
          kids={kids}
        />
      ))}

      {capabilities.canAdd && (
        <InlineStack>
          <Button onClick={addStory}>Add story</Button>
        </InlineStack>
      )}

      {canChangeShipping && shippingOptions.length > 0 && (
        <Select
          label="Shipping method"
          options={shippingOptions.map(option => ({
            value: option.code,
            label: `${option.title} (${option.price} EGP)`
          }))}
          value={draft.shipping?.code || 'regular_shipping'}
          onChange={code => change(current => ({
            ...current,
            shipping: { ...current.shipping, code }
          }))}
        />
      )}

      <EditModeBar
        changeCount={changeCount}
        total={total}
        reviewDisabled={errors.length > 0}
        onReview={() => { setSaveError(''); setConflict(false); setReviewing(true); }}
        onCancel={onExit}
      />

      <OrderEditReview
        open={reviewing}
        order={order}
        preview={preview}
        status={status}
        error={previewError}
        discount={draft.discount}
        onDiscountChange={discount => change(current => ({ ...current, discount }))}
        onBack={() => setReviewing(false)}
        onSave={handleSave}
        saving={saving}
        saveError={saveError}
        draftErrors={errors}
      />
    </BlockStack>
  );
}
